import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Trash2, Calendar, User, Edit, Download, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tasks = ({ user, selectedArtist }) => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    artist_id: '',
    due_date: '',
    is_asap: false,
  });

  const fetchTasks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = selectedArtist ? `?artist_id=${selectedArtist}` : '';
      const response = await axios.get(`${API}/tasks${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  }, [selectedArtist]);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      // Silent fail - users list is supplementary data
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch users:', error.message);
      }
    }
  }, []);

  const fetchArtists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch (error) {
      // Silent fail - artists list is supplementary data
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch artists:', error.message);
      }
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    if (user?.role === 'manager' || user?.role === 'admin') {
      fetchArtists();
    }
  }, [fetchTasks, fetchUsers, fetchArtists, user?.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        artist_id: formData.artist_id || selectedArtist || null,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        is_asap: formData.is_asap || false,
      };
      
      if (editingTask) {
        // Update existing task
        await axios.patch(`${API}/tasks/${editingTask.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Task updated successfully!');
      } else {
        // Create new task
        await axios.post(`${API}/tasks`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Task created successfully!');
      }
      
      setOpen(false);
      setEditingTask(null);
      setFormData({ title: '', description: '', assigned_to: '', artist_id: '', due_date: '', is_asap: false });
      fetchTasks();
    } catch (error) {
      toast.error(editingTask ? 'Failed to update task' : 'Failed to create task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      artist_id: task.artist_id || '',
      due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 10) : '',
      is_asap: task.is_asap || false,
    });
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    setEditingTask(null);
    setFormData({ title: '', description: '', assigned_to: '', artist_id: '', due_date: '', is_asap: false });
  };

  const toggleComplete = async (taskId, completed) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/tasks/${taskId}`,
        { completed: !completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTasks();
      toast.success(completed ? 'Task marked as incomplete' : 'Task completed!');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Task deleted successfully');
      await fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ token });
      if (selectedArtist) {
        params.append('artist_id', selectedArtist);
      }
      
      // Create a hidden link and click it - more reliable across browsers including Safari
      const downloadUrl = `${API}/tasks/pdf/download?${params.toString()}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('PDF opened in new tab');
    } catch (error) {
      toast.error('Failed to open PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailAddress.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/tasks/email`,
        {
          recipient_email: emailAddress,
          artist_id: selectedArtist || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Tasks sent to ${emailAddress}`);
      setEmailDialogOpen(false);
      setEmailAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">Tasks</h1>
          <p className="text-muted-foreground text-base mt-2">Manage your weekly to-do list</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Download PDF Button */}
          {tasks.length > 0 && (
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="border-primary/50 hover:bg-primary/10"
              data-testid="download-tasks-pdf-button"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloadingPdf ? 'Downloading...' : 'Download PDF'}
            </Button>
          )}
          
          {/* Email Tasks Button */}
          {tasks.length > 0 && (user?.role === 'manager' || user?.role === 'admin') && (
            <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-primary/50 hover:bg-primary/10"
                  data-testid="email-tasks-button"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Tasks
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border/50 max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-manrope font-bold text-xl">
                    Send Tasks via Email
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSendEmail} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Recipient Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      placeholder="Enter email address"
                      className="bg-input/50 border-border rounded-xl"
                      required
                      data-testid="email-tasks-input"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    The recipient will receive a summary email with a PDF attachment containing {selectedArtist ? "this artist's" : 'all'} tasks.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEmailDialogOpen(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={sendingEmail}
                      className="flex-1 bg-primary hover:bg-primary/90"
                      data-testid="send-tasks-email-button"
                    >
                      {sendingEmail ? 'Sending...' : 'Send Email'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
          
          {/* Add Task Button */}
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
              data-testid="add-task-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="font-manrope font-bold text-2xl">
                {editingTask ? 'Edit Task' : 'Create New Task'}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto pr-2">
              <form onSubmit={handleSubmit} className="space-y-4 pb-4">
              <div className="space-y-2">
                <Label htmlFor="title">Task Title</Label>
                <Input
                  id="title"
                  data-testid="task-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="task-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-input/50 border-border rounded-xl min-h-24"
                />
              </div>
              {user?.role === 'manager' || user?.role === 'admin' && (
                <div className="space-y-2">
                  <Label htmlFor="artist_id">Artist</Label>
                  <select
                    id="artist_id"
                    data-testid="task-artist-select"
                    value={formData.artist_id || selectedArtist || ''}
                    onChange={(e) => setFormData({ ...formData, artist_id: e.target.value })}
                    className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  >
                    <option value="">Select artist (optional)</option>
                    {artists.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="assigned_to">Assign To</Label>
                <select
                  id="assigned_to"
                  data-testid="task-assign-select"
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                >
                  <option value="">Select user (optional)</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.role})
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <div className="flex gap-3 items-center">
                  <Input
                    id="due_date"
                    type="date"
                    data-testid="task-due-date-input"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value, is_asap: false })}
                    className="bg-input/50 border-border rounded-xl flex-1"
                    disabled={formData.is_asap}
                  />
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="is_asap"
                      checked={formData.is_asap}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_asap: checked, due_date: checked ? '' : formData.due_date })}
                      data-testid="task-asap-checkbox"
                    />
                    <Label htmlFor="is_asap" className="text-sm font-medium text-red-400 cursor-pointer">
                      ASAP
                    </Label>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-task-button"
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {editingTask ? 'Update Task' : 'Create Task'}
                </Button>
              </div>
            </form>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid gap-4">
        {tasks.length === 0 ? (
          <Card className="bg-card border-border/50 p-12 text-center">
            <p className="text-muted-foreground">No tasks yet. Create your first task to get started!</p>
          </Card>
        ) : (
          [...tasks]
            .sort((a, b) => {
              // Completed tasks go to the bottom
              if (a.completed !== b.completed) return a.completed ? 1 : -1;
              // ASAP tasks go to the very top
              if (a.is_asap !== b.is_asap) return a.is_asap ? -1 : 1;
              // Then sort by due date (earliest first, no date at end)
              if (!a.due_date && !b.due_date) return 0;
              if (!a.due_date) return 1;
              if (!b.due_date) return -1;
              return new Date(a.due_date) - new Date(b.due_date);
            })
            .map((task) => {
            const assignedUser = users.find((u) => u.id === task.assigned_to);
            const dueDate = task.due_date ? new Date(task.due_date) : null;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isOverdue = dueDate && dueDate < today && !task.completed;
            const isToday = dueDate && dueDate.toDateString() === today.toDateString();
            const isTomorrow = dueDate && dueDate.toDateString() === new Date(today.getTime() + 86400000).toDateString();
            const isAsap = task.is_asap && !task.completed;
            
            return (
              <Card
                key={task.id}
                className={`bg-card border-border/50 p-6 hover:border-primary/50 transition-colors ${isAsap ? 'border-red-500/70 bg-red-500/10' : ''} ${isOverdue ? 'border-red-500/50 bg-red-500/5' : ''} ${isToday ? 'border-yellow-500/50 bg-yellow-500/5' : ''}`}
                data-testid={`task-item-${task.id}`}
              >
                <div className="flex flex-col">
                  {/* Header with Edit button */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4 flex-1">
                      <Checkbox
                        checked={task.completed}
                        onCheckedChange={() => toggleComplete(task.id, task.completed)}
                        data-testid={`task-checkbox-${task.id}`}
                        className="mt-1"
                      />
                      <div className="flex items-center gap-2">
                        {(() => {
                          const creator = users.find((u) => u.id === task.created_by);
                          const isArtist = creator?.role === 'artist';
                          return (
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center font-manrope font-bold text-sm ${
                                isArtist
                                  ? 'bg-blue-500/20 text-blue-500 border-2 border-blue-500/50'
                                  : 'bg-purple-500/20 text-purple-500 border-2 border-purple-500/50'
                              }`}
                              title={isArtist ? 'Artist Task' : 'Manager Task'}
                              data-testid={`task-role-${task.id}`}
                            >
                              {isArtist ? 'A' : 'M'}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className={`font-manrope font-semibold text-lg ${
                            task.completed ? 'line-through text-muted-foreground' : 'text-foreground'
                          }`}
                        >
                          {task.title}
                        </h3>
                        {task.description && (
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
                          {isAsap && (
                            <div className="flex items-center gap-1 px-2 py-1 rounded-md font-bold bg-red-500/30 text-red-400 animate-pulse">
                              <Calendar className="w-3 h-3" />
                              ASAP
                            </div>
                          )}
                          {dueDate && !isAsap && (
                            <div className={`flex items-center gap-1 px-2 py-1 rounded-md font-medium ${
                              isOverdue ? 'bg-red-500/20 text-red-400' : 
                              isToday ? 'bg-yellow-500/20 text-yellow-400' :
                              isTomorrow ? 'bg-orange-500/20 text-orange-400' :
                              'bg-muted/50 text-muted-foreground'
                            }`}>
                              <Calendar className="w-3 h-3" />
                              {isOverdue ? 'OVERDUE: ' : isToday ? 'TODAY: ' : isTomorrow ? 'TOMORROW: ' : ''}
                              {dueDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </div>
                          )}
                          {!dueDate && !isAsap && (
                            <div className="flex items-center gap-1 px-2 py-1 rounded-md font-medium bg-gray-500/20 text-gray-400">
                              <Calendar className="w-3 h-3" />
                              No date set
                            </div>
                          )}
                          {task.artist_id && (() => {
                            const artist = artists.find((a) => a.id === task.artist_id);
                            return artist ? (
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                Artist: <span className="text-blue-400 font-medium">{artist.name}</span>
                              </div>
                            ) : null;
                          })()}
                          {assignedUser && (
                            <div>
                              Assigned to: <span className="text-primary">{assignedUser.name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(task)}
                      className="min-w-[70px] min-h-[36px] touch-none"
                      data-testid={`edit-task-${task.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>

                  {/* Delete button at bottom right - smaller */}
                  <div className="flex justify-end mt-4 pt-4 border-t border-border/30">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteTask(task.id)}
                      className="min-w-[90px] h-8 text-xs touch-none"
                      data-testid={`delete-task-${task.id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Tasks;